/**
 * AreaDetailPanel - Right-side drawer for geographic area details
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { X, Zap, Lightbulb, Loader2, Settings } from 'lucide-react';
import MetricCard from './MetricCard';
import DistributionChart from './DistributionChart';
import TrendChart from './TrendChart';
import { getAreaDetail, getAreaInsights, generateAreaInsights } from '../../services/locationService';
import { PermissionGate } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import { sentimentToPercent } from '../../utils/formatters';

const SENTIMENT_COLORS = {
  positive: '#22c55e', very_positive: '#16a34a',
  neutral: '#eab308',
  negative: '#ef4444', very_negative: '#dc2626',
};

const AreaDetailPanel = ({ city, startDate, endDate, onClose, onOpenAlertConfig }) => {
  const [detail, setDetail] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cityRef = useRef(city);

  // Slide-in animation
  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
    return () => setIsVisible(false);
  }, []);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    const requestCity = city;
    const dateParams = {};
    if (startDate) dateParams.start_date = startDate;
    if (endDate) dateParams.end_date = endDate;
    const [detailRes, insightRes] = await Promise.all([
      getAreaDetail(city, dateParams),
      getAreaInsights(city),
    ]);
    if (cityRef.current !== requestCity) return;
    if (detailRes.success) setDetail(detailRes.data);
    else toast.error(detailRes.error);
    if (insightRes.success) setInsights(insightRes.data);
    setLoading(false);
  }, [city, startDate, endDate]);

  useEffect(() => {
    cityRef.current = city;
    setDetail(null);
    setInsights([]);
    fetchDetail();
  }, [fetchDetail, city]);

  const handleGenerateInsights = async () => {
    setInsightsLoading(true);
    const requestCity = city;
    const res = await generateAreaInsights(city);
    if (cityRef.current !== requestCity) return;
    setInsightsLoading(false);
    if (res.success) {
      setInsights(res.data);
      toast.success('Insights generated');
    } else {
      toast.error(res.error);
    }
  };

  const sentimentChartData = detail
    ? Object.entries(detail.sentiment_distribution || {}).map(([key, value]) => ({
        name: key, value, color: SENTIMENT_COLORS[key] || '#9ca3af',
      }))
    : [];

  const trendChartData = detail
    ? (detail.daily_trend || []).map(d => ({ date: d.date, value: d.count }))
    : [];

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl flex flex-col transition-transform duration-300 ${
        isVisible ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-base font-semibold text-gray-900">{city}</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={onOpenAlertConfig}
              className="text-xs text-gray-600 border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors flex items-center space-x-1"
            >
              <Settings size={12} />
              <span>Configure Alert</span>
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-primary-500" size={24} />
            </div>
          ) : detail ? (
            <div className="p-5 space-y-5">
              {/* Metrics */}
              <div className="grid grid-cols-3 gap-3">
                <a
                  href={`/conversations?city=${encodeURIComponent(city)}${startDate ? `&from_date=${startDate}` : ''}${endDate ? `&to_date=${endDate}` : ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  title={`Open ${detail.total_conversations} conversations for ${city} in a new tab`}
                >
                  <MetricCard label="Conversations" value={detail.total_conversations} color="#3b82f6" />
                </a>
                <MetricCard
                  label="Avg Sentiment"
                  value={detail.avg_sentiment_score != null ? sentimentToPercent(detail.avg_sentiment_score) + '%' : 'N/A'}
                  color={detail.avg_sentiment_score != null && sentimentToPercent(detail.avg_sentiment_score) >= 50 ? '#22c55e' : '#ef4444'}
                />
                <MetricCard
                  label="Active Alerts"
                  value={detail.alert_count}
                  color={detail.alert_count > 0 ? '#ef4444' : '#6b7280'}
                />
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {sentimentChartData.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Sentiment Distribution</h4>
                    <DistributionChart data={sentimentChartData} height={180} />
                  </div>
                )}
                {trendChartData.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Volume Trend (30d)</h4>
                    <TrendChart data={trendChartData} height={180} />
                  </div>
                )}
              </div>

              {/* AI Insights */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase">AI Insights</h4>
                  {/* Generating is a location_service POST (analytics:reports);
                      reading existing insights is not gated. */}
                  <PermissionGate permission={StandardPermission.Analytics.REPORTS}>
                    <button
                      onClick={handleGenerateInsights}
                      disabled={insightsLoading}
                      className="flex items-center space-x-1 text-xs text-primary-600 border border-primary-300 rounded-lg px-2.5 py-1 hover:bg-primary-50 disabled:opacity-50 transition-colors"
                    >
                      {insightsLoading ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                      <span>{insightsLoading ? 'Generating...' : 'Generate Insights'}</span>
                    </button>
                  </PermissionGate>
                </div>

                {insights.length === 0 && !insightsLoading && (
                  <p className="text-sm text-gray-400 italic">No insights yet. Click "Generate Insights" to analyze this area.</p>
                )}

                <div className="space-y-2">
                  {insights.map((insight) => (
                    <div key={insight.id} className="flex items-start space-x-2 bg-amber-50 rounded-lg px-3 py-2">
                      <Lightbulb size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-[10px] font-semibold text-amber-700 uppercase">{insight.insight_type}</span>
                        <p className="text-xs text-gray-700 mt-0.5">{insight.insight_text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AreaDetailPanel;
